/**
 * @file analytics.tsx
 *
 * @fileoverview Performs dispatching of analytics events to a service
 * that will log these events to some tool.
 */

import {
  logAnalyticsEvent,
  logAnalyticsServerEvent,
  pollLogAnalyticsEvent,
  logConversionApiEvent,
} from '~/jobiqo/services/analytics';
import { Maybe } from '~/jobiqo/types/types';
import {
  customEvents,
  CUSTOM_DIMENSIONS,
  CUSTOM_EVENT_TYPES,
  CUSTOM_NUMERIC_DIMENSIONS,
} from './../../../config/analytics';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';

export enum JOBIQO_EVENT_TYPES {
  CLIENT = 'CLIENT',
  SERVER = 'SERVER',
  HYBRID = 'HYBRID',
}

const getAnalyticsConfig = () => getCurrentFrontendConfig().APP;

export const EVENT_TYPES = { ...JOBIQO_EVENT_TYPES, ...CUSTOM_EVENT_TYPES };

export enum JOBIQO_PAGE_TYPES {
  MINISITE = 'minisite',
  ORGANIZATION_PROFILE = 'organization_profile',
  JOB_SEARCH = 'job_search',
  JOB_DETAIL = 'job_detail',
}

export const PAGE_TYPES = JOBIQO_PAGE_TYPES;
export type PageType = JOBIQO_PAGE_TYPES;

export interface EventParameters {
  [key: string]: string;
}

/**
 * Describes what an event can have.
 */
export interface Event {
  /**
   * Event key.
   */
  key: string;
  /**
   * The title of the event.
   */
  description: string;
  /**
   * Events can happen on the client, server or there might be events
   * like page visits that can happen both on the client or on the server (e.g. reload page).
   */
  type: JOBIQO_EVENT_TYPES | CUSTOM_EVENT_TYPES;
}

// Events.
export const EVENTS = {
  ...customEvents,
  JOB_SEARCH: {
    key: 'job_search',
    description: 'Job Search.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_VISIT: {
    key: 'job_visit',
    description: 'Job detail is opened.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_APPLY_START: {
    key: 'job_apply_start',
    description: 'job apply button is clicked.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_APPLY_INTERNAL_SUCCESS_PAGE_VIEW: {
    key: 'job_apply_internal_success_page_view',
    description: 'Internal job apply success page is displayed.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_APPLY_COMPLETE: {
    key: 'job_apply_complete',
    description: 'Complete job application.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_EDIT: {
    key: 'job_edit',
    description: 'Job edit button is clicked.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_SHARE: {
    key: 'job_share',
    description: 'when social media sharing buttons are clicked.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_BOOKMARK: {
    key: 'job_bookmark',
    description: 'when job is successfully bookmarked.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_SEARCH: {
    key: 'resume_search',
    description: 'resume search result page is displayed.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_VISIT: {
    key: 'resume_visit',
    description: 'resume is opened by recruiter.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_REQUEST: {
    key: 'resume_request',
    description: 'resume contact request is successfully created.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_BOOKMARK: {
    key: 'resume_bookmark',
    description: 'resume is successfully bookmarked.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_PRINT: {
    key: 'resume_print',
    description: 'resume print button is clicked.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_EDIT: {
    key: 'resume_edit',
    description: 'resume save button is clicked (any section).',
    type: EVENT_TYPES.CLIENT,
  },
  ORGANIZATION_PROFILE_SEARCH: {
    key: 'orgprofile_search',
    description: 'organizational profile search result page is displayed.',
    type: EVENT_TYPES.CLIENT,
  },
  ORGANIZATION_PROFILE_VISIT: {
    key: 'orgprofile_visit',
    description: 'organizational profile is opened.',
    type: EVENT_TYPES.CLIENT,
  },
  MINISITE_LANDING_PAGE_VISIT: {
    key: 'minisite_landing_page_visit',
    description: 'Mini-site landing page is opened.',
    type: EVENT_TYPES.CLIENT,
  },
  ORGANIZATION_PROFILE_EDIT: {
    key: 'orgprofile_edit',
    description:
      'organizational profile editor "save" button is clicked (any section).',
    type: EVENT_TYPES.CLIENT,
  },
  ORGANIZATION_PROFILE_SHARE: {
    key: 'orgprofile_share',
    description: 'share button on organizational profile is clicked.',
    type: EVENT_TYPES.CLIENT,
  },
  LOGIN: {
    key: 'login',
    description: 'Whenever a user successfully logged in (use method param).',
    type: EVENT_TYPES.CLIENT,
  },
  SIGNUP_START: {
    key: 'sign_up_start',
    description: 'User opens registration form.',
    type: EVENT_TYPES.CLIENT,
  },
  SIGNUP_FINISH: {
    key: 'sign_up',
    description:
      'Should trigger on the email verification page. When user verifies account.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_ALERT_SIGNUP: {
    key: 'job_alert_signup',
    description: 'Singup for a job alert.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_ALERT_SIGNUP_ANONYMOUS: {
    key: 'job_alert_signup_anonymous',
    description: 'Anonymous user singup for a job alert.',
    type: EVENT_TYPES.CLIENT,
  },
  JOB_ALERT_PROMPT: {
    key: 'job_alert_prompt',
    description: 'Prompt for job alert.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_STEP_PERSONAL_INFO: {
    key: 'resume_step_personal_info',
    description: 'Applicant finished personal info step',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_COMPLETED: {
    key: 'resume_completed',
    description: 'Applicant finished resume.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_ALERT_SIGNUP: {
    key: 'resume_alert_signup',
    description: 'Resume alert signup.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_VISIT_ANONYMOUS: {
    key: 'resume_visit_anonymous',
    description: 'Anonymous resume is opened by recruiter.',
    type: EVENT_TYPES.CLIENT,
  },
  RESUME_VISIT_FULL: {
    key: 'resume_visit_full',
    description: 'Resume with access granted is opened by recruiter.',
    type: EVENT_TYPES.CLIENT,
  },
  PAGE_VIEW: {
    key: 'page_view',
    description: 'Event to track all page views.',
    type: EVENT_TYPES.CLIENT,
  },
  QA_BASIC_INFO_PAGE_VIEW: {
    key: 'qa_basic_info_page_view',
    description: 'Quicky job apply basic info page view.',
    type: EVENT_TYPES.CLIENT,
  },
  QA_QUESTIONS_PAGE_VIEW: {
    key: 'qa_questions_page_view',
    description: 'Quicky job apply questions page view.',
    type: EVENT_TYPES.CLIENT,
  },
  QA_FILE_UPLOAD_PAGE_VIEW: {
    key: 'qa_file_upload_page_view',
    description: 'Quicky job apply file upload page view.',
    type: EVENT_TYPES.CLIENT,
  },
  QA_REVIEW_INFO_PAGE_VIEW: {
    key: 'qa_review_info_page_view',
    description: 'Quicky job apply review info page view.',
    type: EVENT_TYPES.CLIENT,
  },
  QA_SUCCESS_PAGE_VIEW: {
    key: 'qa_success_page_view',
    description: 'Quicky job apply success page view.',
    type: EVENT_TYPES.CLIENT,
  },
  VIEW_ORIGINAL_AD_BTN_CLICK: {
    key: 'view_original_ad_btn_click',
    description: 'View original ad button click.',
    type: EVENT_TYPES.CLIENT,
  },
  WEBTREKK_PAGE_VIEW: {
    key: 'webtrekk_page_view',
    description: 'Webtrekk page view.',
    type: EVENT_TYPES.CLIENT,
  },
};

// Dimensions.
enum JOBIQO_DIMENSIONS {
  entity_id = 'entity_id',
  entity_type = 'entity_type',
  entity_bundle = 'entity_bundle',
  entity_title = 'entity_title',
  organization_name = 'organization_name',
  title = 'title',
  application_type = 'application_type',
  importer_ID = 'importer_ID',
  occupations = 'occupations',
  regions = 'regions',
  employment_types = 'employment_types',
  upgrades = 'upgrades',
  user_role = 'user_role',
  current_user_role = 'current_user_role',
  current_user_id = 'current_user_id',
  route = 'route',
  entity_subtype = 'entity_subtype',
  owner_id = 'owner_id',
  entity_user_id = 'entity_user_id',
  entity_user_name = 'entity_user_name',
  event_occurrence_place = 'event_occurrence_place',
  companies = 'companies',
  search_query = 'search_query',
  organization_id = 'organization_id',
  signup_method = 'signup_method',
  prefered_job_role = 'prefered_job_role',
  languages = 'languages',
  skills = 'skills',
  region = 'region',
  administrative_area = 'administrative_area',
  fields_of_study = 'fields_of_study',
  request_status = 'request_status',
  state = 'state',
  job_owner_frontend = 'job_owner_frontend',
  page_type = 'page_type',
  salary_low = 'salary_low',
  salary_high = 'salary_high',
  salary_currency = 'salary_currency',
  job_age = 'job_age',
  share_method = 'share_method',
  product_type = 'product_type',
  industry = 'industry',
}
export const DIMENSIONS = { ...JOBIQO_DIMENSIONS, ...CUSTOM_DIMENSIONS };

const NUMERIC_DIMENSIONS: string[] = [
  DIMENSIONS.entity_id,
  DIMENSIONS.current_user_id,
  DIMENSIONS.owner_id,
  DIMENSIONS.entity_user_id,
  DIMENSIONS.organization_id,
  DIMENSIONS.importer_ID,
  DIMENSIONS.salary_low,
  DIMENSIONS.salary_high,
  ...CUSTOM_NUMERIC_DIMENSIONS,
];

/**
 * Calls the service to log a specific event.
 *
 * @param {Event} event
 *  Dispatch event.
 * @param {EventParameters} params
 *  Event parameters.
 */
export const analyticsDispatch = (
  event: Event,
  params: EventParameters = {},
) => {
  logAnalyticsEvent(event, params);
};

/**
 * We need to make sure that gtag is loaded, so we delay dispatching events for 5 seconds.
 * This function should be called only for events that are triggered close to
 * DOM loading events and not used for events triggered manually after user interaction because,
 * at that point, we can be almost sure that the gtag is loaded.
 * After delay, it calls the service to log a specific event.
 *
 * @param {Event} event
 *  Dispatch event.
 * @param {EventParameters} params
 *  Event parameters.
 */
export const analyticsDispatchDelayed = (
  event: Event,
  params: EventParameters = {},
) => {
  const analyticsConfig = getAnalyticsConfig();
  pollLogAnalyticsEvent(event, params);
  // For SST analytics we need to dispatch the event immediately.
  if (analyticsConfig.GOOGLE_MEASUREMENT_API_ENABLED) {
    logAnalyticsServerEvent(event, params);
  }

  // Send the event to the conversion API if it is enabled and the event is in the list.
  if (
    analyticsConfig.CONVERSION_API_ENABLED &&
    analyticsConfig.CONVERSION_API_ENABLED_EVENTS.includes(event.key)
  ) {
    // Rework the event parameters to match the conversion API.
    logConversionApiEvent(event, params);
  }
};

export interface Dimensions {
  /**
   * The key is any GA event dimension, and the value can
   * be string, number, or falsy.
   */
  [key: string]: Maybe<string> | number | undefined;
}

// Defines an array of dimensions that we do not want to send to GA,
// if the value is falsy.
const SKIPPED_DIMENSIONS_IF_FALSY: string[] = [];

/**
 * Populates all values for dimension object, if the value of some
 * property is not defined, it will be set to default value.
 * @param {Dimension} params
 * @returns {EventParameters}
 * Returns appropriate event parameters that are sent to GA.
 */
export function setEventParams(params: Dimensions): EventParameters {
  const optionalParams: EventParameters = {};

  Object.entries(params).forEach(([key, value]) => {
    if (SKIPPED_DIMENSIONS_IF_FALSY.includes(key) && !value) {
      return;
    }

    optionalParams[key] = value
      ? typeof value === 'string'
        ? value
        : value.toString()
      : getDefaultDimensionValue(key);
  });

  return optionalParams;
}

// Default dimension values.
type DefaultDimensionType = '-1' | '(none)';

/**
 * Returns default dimension value for a given dimension key.
 * @param {string} key
 * @returns {DefaultDimensionType}
 * Returns default dimension value.
 */
function getDefaultDimensionValue(key: string): DefaultDimensionType {
  return NUMERIC_DIMENSIONS.includes(key) ? '-1' : '(none)';
}
